import { configForEnvironment, type AppEnvironment } from "@whitehaven/config";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createHomepageEditorApiClient,
  HomepageEditorApiError,
  type HomepageEditorApiClient,
  type PortalSessionData,
} from "./homepage-editor-api";
import { registerProtectedStateCleaner } from "../../protected-state";

export type PortalSessionStatus =
  | "loading"
  | "authenticated"
  | "authentication-required"
  | "access-denied"
  | "error";

export interface PortalSessionState {
  readonly status: PortalSessionStatus;
  readonly data: PortalSessionData | null;
  readonly message: string | null;
  readonly correlationId: string | null;
}

export interface PortalSessionController extends PortalSessionState {
  readonly client: HomepageEditorApiClient;
  readonly retry: () => void;
}

const initialState: PortalSessionState = {
  status: "loading",
  data: null,
  message: null,
  correlationId: null,
};

export function portalEnvironmentForMode(mode: string): AppEnvironment {
  if (mode === "production") return "production";
  if (mode === "staging") return "staging";
  return "development";
}

export function portalApiUrlForMode(mode: string): string {
  return configForEnvironment(portalEnvironmentForMode(mode)).apiUrl;
}

export function usePortalSession(
  providedClient?: HomepageEditorApiClient,
): PortalSessionController {
  const client = useMemo(
    () =>
      providedClient ??
      createHomepageEditorApiClient(portalApiUrlForMode(import.meta.env.MODE)),
    [providedClient],
  );
  const [state, setState] = useState<PortalSessionState>(initialState);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  useEffect(
    () =>
      registerProtectedStateCleaner("homepage-editor", () => {
        setState({
          status: "authentication-required",
          data: null,
          message: "The secure session ended.",
          correlationId: null,
        });
      }),
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    setState(initialState);

    void client
      .loadSession(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({
          status: "authenticated",
          data,
          message: null,
          correlationId: null,
        });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        if (caught instanceof DOMException && caught.name === "AbortError") {
          return;
        }
        if (
          caught instanceof HomepageEditorApiError &&
          caught.isAuthenticationRequired
        ) {
          setState({
            status: "authentication-required",
            data: null,
            message: "Sign in with an authorized Whitehaven account.",
            correlationId: caught.correlationId,
          });
          return;
        }
        if (caught instanceof HomepageEditorApiError && caught.isAccessDenied) {
          setState({
            status: "access-denied",
            data: null,
            message: caught.message,
            correlationId: caught.correlationId,
          });
          return;
        }
        setState({
          status: "error",
          data: null,
          message:
            caught instanceof HomepageEditorApiError
              ? caught.message
              : "The secure portal API is unavailable.",
          correlationId:
            caught instanceof HomepageEditorApiError
              ? caught.correlationId
              : null,
        });
      });

    return () => controller.abort();
  }, [attempt, client]);

  return { ...state, client, retry };
}
